package nl.volleyplanner.web.component

import kotlinx.browser.document
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.dom.addClass
import kotlinx.dom.clear
import kotlinx.dom.removeClass
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.div
import kotlinx.html.js.onChangeFunction
import kotlinx.html.js.onClickFunction
import kotlinx.serialization.KSerializer
import kotlinx.serialization.json.Json
import nl.volleyplanner.api.client.FileUploadException
import nl.volleyplanner.api.files.FileUploadResponse
import nl.volleyplanner.web.MainApp
import nl.volleyplanner.web.VolleyPlannerApplication
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.launchAsync
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLInputElement

enum class FileUploadState {
    NotStarted,
    Uploading,
    Finished
}

data class FileUploadResult(
    val success: Boolean,
    val response: FileUploadResponse?
)

data class FileUploadProgress(
    val fileUploadState: FileUploadState,
    val result: FileUploadResult?
)

class FileUploadResultsComponent<T : FileUploadResponse>(private val progressFlow: StateFlow<FileUploadProgress>, private val successBlock: DIV.(T) -> Unit) : HTMLComponent() {
    val progress: FileUploadProgress
        get() = progressFlow.value

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.div (classes = "border-secondary") {
            if (progress.fileUploadState == FileUploadState.Uploading) {
                spinner()
            } else if (progress.fileUploadState == FileUploadState.Finished) {
                val result = progress.result!!
                div {
                    strong { +"Status: " }
                    if (result.success) +"Bestand verwerkt" else +"Verwerking mislukt"
                }
                if (!result.success) {
                    div {
                        if (result.response != null) {
                            result.response.handlingExceptions.forEach { exception ->
                                +exception
                                br {}
                            }
                        } else {
                            +"Onbekende fout opgetreden"
                        }
                    }
                } else {
                    @Suppress("UNCHECKED_CAST")
                    successBlock(result.response as T)
                }
            }
        }
    }

    override suspend fun getState() = progressFlow
}

class FileUploadComponent<T : FileUploadResponse>(
    val uploadId: String,
    val fileType: String,
    val acceptFormats: List<String>,
    val responseSerializer: KSerializer<T>,
    val successBlock: DIV.(T) -> Unit,
    val customFields: Map<String, String> = emptyMap()
) : HTMLComponent() {
    private val formId = "vp-file-upload-$uploadId"
    private val progressFlow = MutableStateFlow(FileUploadProgress(FileUploadState.NotStarted, null))

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.div {
            form {
                id = formId
                customFields.forEach { (fieldName, fieldValue) ->
                    input {
                        type = InputType.hidden
                        name = fieldName
                        value = fieldValue
                    }
                }
                input (classes = "form-control mb-1") {
                    type = InputType.file
                    name = "file_upload"
                    // Ensure all formats start with a .
                    accept = acceptFormats.map { ".${it.removePrefix(".")}" }.joinToString { "," }
                    onChangeFunction = { event ->
                        progressFlow.value = progressFlow.value.copy(fileUploadState = FileUploadState.NotStarted, null)
                    }
                }
                input (classes = "btn btn-primary") {
                    type = InputType.submit

                    onClickFunction = { event ->
                        event.preventDefault()
                        val buttonElement = event.currentTarget as HTMLInputElement

                        val formElement = document.getElementById(formId)!! as HTMLFormElement
                        val fileInputElement = formElement.elements.namedItem("file_upload")!! as HTMLInputElement

                        val selectedFile = fileInputElement.files!!.item(0)
                        if (selectedFile == null) {
                            buttonElement.removeClass("btn-primary")
                            buttonElement.addClass("btn-danger")
                        }
                        /**
                         * 1. Upload File
                         * 2. Trigger import job
                         * 3. Track results
                         */
                        progressFlow.value = progressFlow.value.copy(fileUploadState = FileUploadState.Uploading, result = null)
                        launchAsync {
                            VolleyPlannerApplication.volleyplannerApiFileUploadClient
                                .uploadFile(fileType, selectedFile!!,
                                    { json ->
                                        Json.decodeFromString(responseSerializer, json)
                                    },
                                    customFields = customFields
                                )
                        }.onComplete { data ->
                            progressFlow.value = progressFlow.value.copy(
                                fileUploadState = FileUploadState.Finished,
                                result = FileUploadResult(success = data.succeeded, response = data)
                            )
                        }.onError { error ->
                            if (error is FileUploadException) {
                                progressFlow.value = progressFlow.value.copy(
                                    fileUploadState = FileUploadState.Finished,
                                    result = FileUploadResult(success = false, response = error.response)
                                )
                            } else {
                                progressFlow.value = progressFlow.value.copy(
                                    fileUploadState = FileUploadState.Finished,
                                    result = FileUploadResult(success = false, response = null)
                                )
                                throw error
                            }
                        }
                    }

                    value = "Uploaden"
                }
            }
            component(FileUploadResultsComponent(progressFlow, successBlock))
        }
    }

    override suspend fun getState() = null
}